import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Wallet, Calendar, AlertTriangle, ArrowRight, Clock } from "lucide-react";
import { format, parseISO, differenceInDays } from "date-fns";

export default function Dashboard() {
    const { user } = useAuth();
    const [credits, setCredits] = useState(null);
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const load = async () => {
            try {
                const [c, b] = await Promise.all([
                    api.get("/credits/me"),
                    api.get("/bookings/me"),
                ]);
                setCredits(c.data);
                setBookings(b.data);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, []);

    const upcoming = bookings
        .filter((b) => b.status === "confirmed" && b.date >= new Date().toISOString().slice(0, 10))
        .slice(0, 5);

    return (
        <div className="px-6 md:px-12 lg:px-16 py-10 max-w-7xl mx-auto">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                <div>
                    <p className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500 mb-2">Dashboard</p>
                    <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-slate-900">
                        Hi, {user?.name?.split(" ")[0] || "there"}.
                    </h1>
                    <p className="text-base text-slate-600 mt-2">Your bookings and credit overview.</p>
                </div>
                <div className="flex gap-3">
                    <Link to="/calendar">
                        <Button data-testid="dashboard-book-btn" className="bg-brand hover:bg-brand-hover text-white rounded-md h-11 group">
                            Book a pod
                            <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
                        </Button>
                    </Link>
                    <Link to="/credits">
                        <Button variant="outline" className="rounded-md h-11" data-testid="dashboard-buy-credits-btn">Buy credits</Button>
                    </Link>
                </div>
            </div>

            {/* KPI Bento */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
                <KPICard
                    icon={Wallet}
                    label="Credit balance"
                    value={loading ? "—" : credits?.balance ?? 0}
                    sub={`${credits?.expiring_soon ?? 0} expiring within 7 days`}
                    testid="kpi-balance"
                />
                <KPICard
                    icon={Calendar}
                    label="Upcoming bookings"
                    value={loading ? "—" : upcoming.length}
                    sub={upcoming[0] ? `Next: ${format(parseISO(upcoming[0].date), "MMM d")}` : "None scheduled"}
                    testid="kpi-upcoming"
                />
                <KPICard
                    icon={AlertTriangle}
                    label="No-show count"
                    value={user?.no_show_count ?? 0}
                    sub={(user?.no_show_count ?? 0) === 0 ? "First waiver still available" : "Future no-shows forfeit credits"}
                    testid="kpi-no-show"
                />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                {/* Upcoming bookings list */}
                <div className="lg:col-span-2 bg-white border border-slate-200 rounded-md p-6 md:p-8">
                    <div className="flex items-center justify-between">
                        <h2 className="font-heading text-xl font-bold text-slate-900">Upcoming sessions</h2>
                        <Link to="/bookings" className="text-xs uppercase tracking-[0.16em] font-mono text-brand hover:underline" data-testid="dashboard-view-all-bookings">All bookings →</Link>
                    </div>
                    <div className="mt-5 divide-y divide-slate-100">
                        {upcoming.length === 0 && (
                            <div className="text-sm text-slate-500 py-8 text-center">No upcoming bookings. Reserve your first pod from the calendar.</div>
                        )}
                        {upcoming.map((b) => (
                            <div key={b.id} className="flex items-center justify-between py-4">
                                <div>
                                    <div className="font-heading font-semibold text-slate-900">{format(parseISO(b.date), "EEEE, MMM d, yyyy")}</div>
                                    <div className="text-xs uppercase tracking-[0.16em] font-mono text-slate-500 mt-0.5">
                                        {b.slot === "morning" && "Morning · 8:00 AM – 12:00 PM"}
                                        {b.slot === "afternoon" && "Afternoon · 1:00 PM – 5:00 PM"}
                                        {b.slot === "full_day" && "Full Day · 8:00 AM – 5:00 PM"}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2 text-xs">
                                    <span className="px-2 py-1 bg-brand/10 text-brand font-mono uppercase tracking-[0.14em] rounded-sm">
                                        −{b.credits_used} cr
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Credit batches */}
                <div className="bg-white border border-slate-200 rounded-md p-6 md:p-8">
                    <h2 className="font-heading text-xl font-bold text-slate-900">Credit lots</h2>
                    <p className="text-sm text-slate-500 mt-1">Earliest expiry used first.</p>
                    <div className="mt-5 space-y-3">
                        {(credits?.batches || []).length === 0 && (
                            <div className="text-sm text-slate-500 py-4">No credits yet.</div>
                        )}
                        {(credits?.batches || []).slice(0, 6).map((b) => {
                            const days = differenceInDays(parseISO(b.expires_at), new Date());
                            const warn = days <= 7;
                            return (
                                <div key={b.id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                                    <div>
                                        <div className="font-mono text-sm text-slate-900 font-medium">{b.amount} credit{b.amount !== 1 ? "s" : ""}</div>
                                        <div className="text-[11px] uppercase tracking-[0.14em] font-mono text-slate-500">{b.source.replace("_", " ")}</div>
                                    </div>
                                    <div className={`flex items-center gap-1.5 text-xs font-mono ${warn ? "text-amber-600" : "text-slate-500"}`}>
                                        <Clock className="h-3 w-3" />
                                        {days <= 0 ? "expired" : `${days}d left`}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}

function KPICard({ icon: Icon, label, value, sub, testid }) {
    return (
        <div data-testid={testid} className="bg-white border border-slate-200 rounded-md p-7 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300">
            <div className="flex items-center justify-between">
                <span className="text-[11px] uppercase tracking-[0.2em] font-mono text-slate-500">{label}</span>
                <Icon className="h-4 w-4 text-slate-400" strokeWidth={1.75} />
            </div>
            <div className="mt-4 font-heading text-4xl font-bold tracking-tighter text-slate-900">{value}</div>
            <div className="mt-2 text-sm text-slate-500">{sub}</div>
        </div>
    );
}
